<template>
  <v-dialog v-model="dialog" max-width="1100px" scrollable persistent>
    <v-card>
      <v-card-title class="primary--text d-flex align-center flex-wrap">
        <span class="mr-3">Participantes - {{ tournament.name }}</span>
        <v-chip small :color="statusInfo.color" text-color="white">{{ statusInfo.text }}</v-chip>
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <!-- Enlace público -->
      <div v-if="tournament.public_registration_url" class="px-6 pb-2">
        <v-text-field
          :value="tournament.public_registration_url"
          label="Enlace público de registro"
          outlined
          dense
          readonly
          hide-details
          prepend-inner-icon="mdi-link-variant"
          append-icon="mdi-content-copy"
          @click:append="copyLink"
        ></v-text-field>
      </div>
      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <!-- Estadísticas -->
        <v-row v-if="stats" dense class="mb-4">
          <v-col v-for="card in statCards" :key="card.label" cols="6" sm="4" md="2">
            <v-sheet outlined rounded class="pa-3 text-center">
              <div class="text-h5 font-weight-bold" :class="card.color + '--text'">{{ card.value }}</div>
              <div class="text-caption grey--text">{{ card.label }}</div>
            </v-sheet>
          </v-col>
        </v-row>

        <!-- Acciones -->
        <v-row dense class="mb-2" align="center">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="search"
              label="Buscar por nombre, club, teléfono o correo"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="6" md="2">
            <v-select
              v-model="filters.attended"
              :items="attendedOptions"
              label="Asistencia"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="6" md="2">
            <v-select
              v-model="filters.invited"
              :items="invitedOptions"
              label="Invitación"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" md="4" class="text-md-right">
            <v-btn small outlined color="primary" class="mr-2 mb-1" @click="showImport = !showImport">
              <v-icon small left>mdi-file-upload</v-icon>
              Importar
            </v-btn>
            <v-btn small outlined color="primary" class="mr-2 mb-1" @click="$emit('add-player', tournament)">
              <v-icon small left>mdi-account-plus</v-icon>
              Agregar
            </v-btn>
            <v-btn
              v-if="!isKids"
              small
              color="primary"
              class="mb-1"
              :disabled="!pendingCount || sending"
              :loading="sending"
              @click="sendPending"
            >
              <v-icon small left>mdi-send</v-icon>
              Enviar pendientes ({{ pendingCount }})
            </v-btn>
          </v-col>
        </v-row>

        <!-- Progreso de envío -->
        <v-alert v-if="sendReport" :type="sendReport.failed ? 'warning' : 'success'" dense text class="mb-4">
          <div>
            Enviadas: <strong>{{ sendReport.sent }}</strong>
            <span v-if="sendReport.failed"> · Fallidas: <strong>{{ sendReport.failed }}</strong></span>
            <span v-if="sendReport.remaining"> · Pendientes: <strong>{{ sendReport.remaining }}</strong></span>
          </div>
          <div v-if="sendReport.failedNames.length" class="text-caption mt-1">
            No se pudo enviar a: {{ sendReport.failedNames.join(', ') }}. Puedes reintentar desde el menú de cada fila.
          </div>
        </v-alert>

        <!-- Importación -->
        <v-expand-transition>
          <v-sheet v-if="showImport" outlined rounded class="pa-4 mb-4">
            <div class="subtitle-2 font-weight-bold mb-2">Importar participantes (CSV o Excel)</div>
            <div class="text-caption grey--text mb-3">
              Columnas: <strong>Nombre</strong>, <strong>Club</strong> y <strong>Correo</strong> / <strong>Teléfono</strong>
              (o una sola columna "Correo electrónico o teléfono"). Volver a cargar el mismo archivo no
              duplica registros. Importar no envía mensajes.
            </div>
            <v-file-input
              v-model="importFile"
              accept=".csv,.xlsx,.xlsm"
              label="Archivo"
              outlined
              dense
              hide-details
              prepend-icon="mdi-paperclip"
              class="mb-3"
              @change="importReport = null"
            ></v-file-input>
            <div>
              <v-btn small outlined color="primary" class="mr-2" :disabled="!importFile" :loading="importing" @click="runImport(true)">
                Validar
              </v-btn>
              <v-btn small color="primary" :disabled="!importFile || !importReport || importReport.dry_run === false" :loading="importing" @click="runImport(false)">
                Importar
              </v-btn>
            </div>
            <v-alert v-if="importReport" :type="importReport.errors.length ? 'warning' : 'success'" dense text class="mt-3 mb-0">
              <div>
                {{ importReport.dry_run ? 'Validación' : 'Importación' }}:
                <strong>{{ importReport.created }}</strong> nuevos ·
                <strong>{{ importReport.updated }}</strong> existentes ·
                <strong>{{ importReport.skipped }}</strong> omitidos
              </div>
              <ul v-if="importReport.errors.length" class="text-caption mt-2 mb-0">
                <li v-for="(e, i) in importReport.errors.slice(0, 10)" :key="i">
                  Fila {{ e.line }}: {{ e.error }}
                </li>
              </ul>
            </v-alert>
          </v-sheet>
        </v-expand-transition>

        <!-- Tabla -->
        <v-data-table
          :headers="headers"
          :items="members"
          :loading="loading"
          :server-items-length="total"
          :options.sync="options"
          :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
          no-data-text="No hay participantes con estos filtros"
          class="elevation-0"
        >
          <template #item.full_name="{ item }">
            <div class="font-weight-bold">{{ item.full_name }}</div>
            <div class="text-caption grey--text">
              {{ methodLabel(item.registration_method) }}
              <span v-if="item.guest_count"> · {{ item.guest_count }} acompañante(s)</span>
            </div>
          </template>
          <template #item.contact="{ item }">
            <div class="text-caption">{{ item.phone || '—' }}</div>
            <div class="text-caption grey--text">{{ item.email || '—' }}</div>
          </template>
          <template #item.invitation_sent_at="{ item }">
            <v-chip v-if="item.invitation_sent_at" x-small color="success" text-color="white">
              Enviada {{ shortDate(item.invitation_sent_at) }}
            </v-chip>
            <v-chip v-else-if="!item.phone && !item.email" x-small color="grey" text-color="white">Sin contacto</v-chip>
            <v-chip v-else x-small color="warning" text-color="white">Pendiente</v-chip>
          </template>
          <template #item.attended="{ item }">
            <v-icon small :color="item.attended ? 'success' : 'grey lighten-1'">
              {{ item.attended ? 'mdi-check-circle' : 'mdi-circle-outline' }}
            </v-icon>
          </template>
          <template #item.status="{ item }">
            <v-chip x-small :color="memberStatus(item.status).color" text-color="white">
              {{ memberStatus(item.status).text }}
            </v-chip>
          </template>
          <template #item.actions="{ item }">
            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" :loading="busyId === item.id" v-on="on">
                  <v-icon small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item :disabled="!item.phone" @click="resend(item, ['whatsapp'])">
                  <v-list-item-icon><v-icon small>mdi-whatsapp</v-icon></v-list-item-icon>
                  <v-list-item-title>Reenviar por WhatsApp</v-list-item-title>
                </v-list-item>
                <v-list-item :disabled="!item.email || isKids" @click="resend(item, ['email'])">
                  <v-list-item-icon><v-icon small>mdi-email-outline</v-icon></v-list-item-icon>
                  <v-list-item-title>Reenviar por correo</v-list-item-title>
                </v-list-item>
                <v-list-item @click="resend(item)">
                  <v-list-item-icon><v-icon small>mdi-send</v-icon></v-list-item-icon>
                  <v-list-item-title>Reenviar por todos los canales</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item :disabled="item.attended" @click="markAttended(item)">
                  <v-list-item-icon><v-icon small>mdi-check</v-icon></v-list-item-icon>
                  <v-list-item-title>Marcar asistencia</v-list-item-title>
                </v-list-item>
                <v-list-item :disabled="item.status === 3" @click="cancelMember(item)">
                  <v-list-item-icon><v-icon small color="error">mdi-account-cancel</v-icon></v-list-item-icon>
                  <v-list-item-title class="error--text">Cancelar registro</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-btn text :loading="loading" @click="refresh">
          <v-icon small left>mdi-refresh</v-icon>
          Actualizar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('close')">Cerrar</v-btn>
      </v-card-actions>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" top>
        {{ snackbar.text }}
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import { tournamentsAPI } from '@/services/tournaments'
import { getTournamentStatus } from '../constants/tournamentConfig'

const REGISTRATION_STATUS = {
  1: { text: 'Pendiente', color: 'warning' },
  2: { text: 'Confirmado', color: 'success' },
  3: { text: 'Cancelado', color: 'grey' },
  4: { text: 'No asistió', color: 'error' }
}

const METHOD_LABELS = {
  self: 'Inscripción web',
  admin: 'Inscrito por administración',
  public_link: 'Enlace público',
  import: 'Importado'
}

export default {
  name: 'ParticipantsDialog',
  props: {
    tournament: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      dialog: true,
      loading: false,
      members: [],
      total: 0,
      stats: null,
      search: '',
      filters: { attended: null, invited: null },
      options: { page: 1, itemsPerPage: 25 },
      searchTimer: null,
      showImport: false,
      importFile: null,
      importing: false,
      importReport: null,
      sending: false,
      sendReport: null,
      busyId: null,
      snackbar: { show: false, text: '', color: 'info' },
      attendedOptions: [
        { text: 'Todos', value: null },
        { text: 'Asistieron', value: 'true' },
        { text: 'No han llegado', value: 'false' }
      ],
      invitedOptions: [
        { text: 'Todos', value: null },
        { text: 'Enviada', value: 'true' },
        { text: 'Pendiente', value: 'false' }
      ],
      headers: [
        { text: 'Participante', value: 'full_name', sortable: false },
        { text: 'Club', value: 'club', sortable: false },
        { text: 'Contacto', value: 'contact', sortable: false },
        { text: 'Estado', value: 'status', sortable: false, width: '110px' },
        { text: 'Invitación', value: 'invitation_sent_at', sortable: false },
        { text: 'Asistió', value: 'attended', sortable: false, width: '80px', align: 'center' },
        { text: '', value: 'actions', sortable: false, width: '50px' }
      ]
    }
  },
  computed: {
    statusInfo () {
      return getTournamentStatus(this.tournament.status)
    },
    isKids () {
      return this.tournament.tournament_type === 'kids'
    },
    pendingCount () {
      return this.stats ? this.stats.invitations_pending : 0
    },
    statCards () {
      const s = this.stats
      if (!s) return []
      return [
        { label: 'Inscritos', value: `${s.registered}/${s.max_players}`, color: 'primary' },
        { label: 'Acompañantes', value: s.guests, color: 'secondary' },
        { label: 'Invitaciones enviadas', value: s.invitations_sent, color: 'success' },
        { label: 'Pendientes', value: s.invitations_pending, color: 'warning' },
        { label: 'Asistieron', value: s.attended_principals, color: 'success' },
        { label: 'Sin contacto', value: s.without_contact, color: 'error' }
      ]
    }
  },
  watch: {
    options: {
      handler () { this.loadMembers() },
      deep: true
    },
    search () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.resetAndLoad(), 400)
    },
    'filters.attended' () { this.resetAndLoad() },
    'filters.invited' () { this.resetAndLoad() }
  },
  mounted () {
    this.loadStats()
  },
  beforeDestroy () {
    clearTimeout(this.searchTimer)
  },
  methods: {
    resetAndLoad () {
      if (this.options.page !== 1) {
        this.options = { ...this.options, page: 1 } // dispara el watcher de options
      } else {
        this.loadMembers()
      }
    },
    async loadMembers () {
      this.loading = true
      try {
        const params = {
          type: 'principal',
          page: this.options.page,
          page_size: this.options.itemsPerPage
        }
        if (this.search) params.search = this.search
        if (this.filters.attended) params.attended = this.filters.attended
        if (this.filters.invited) params.invited = this.filters.invited

        const { data } = await tournamentsAPI.members(this.tournament.id, params)
        this.members = data.members
        this.total = data.total
      } catch (err) {
        this.notify(this.errorText(err, 'No se pudo cargar la lista'), 'error')
      } finally {
        this.loading = false
      }
    },
    async loadStats () {
      try {
        const { data } = await tournamentsAPI.stats(this.tournament.id)
        this.stats = data
      } catch (err) {
        this.stats = null
      }
    },
    async refresh () {
      await Promise.all([this.loadMembers(), this.loadStats()])
      this.$emit('changed')
    },
    // ---- Importación ----
    async runImport (dryRun) {
      if (!this.importFile) return
      this.importing = true
      try {
        const { data } = await tournamentsAPI.importMembers(this.tournament.id, this.importFile, dryRun)
        this.importReport = data
        if (!dryRun) {
          this.notify(`Importación completa: ${data.created} nuevos, ${data.updated} existentes`, 'success')
          this.importFile = null
          await this.refresh()
        }
      } catch (err) {
        this.importReport = null
        this.notify(this.errorText(err, 'No se pudo procesar el archivo'), 'error')
      } finally {
        this.importing = false
      }
    },
    // ---- Envíos ----
    async sendPending () {
      const confirmText = `¿Enviar la invitación a ${this.pendingCount} participante(s) pendiente(s)?`
      if (!window.confirm(confirmText)) return

      this.sending = true
      this.sendReport = { sent: 0, failed: 0, remaining: this.pendingCount, failedNames: [] }
      const failedIds = new Set()
      try {
        // El API procesa un lote por llamada; se repite hasta vaciar los pendientes.
        // Los que fallaron no se reintentan en esta corrida para no ciclar.
        let guard = 0
        while (this.sendReport.remaining > 0 && guard < 100) {
          guard += 1
          const { data } = await tournamentsAPI.sendInvitations(this.tournament.id, { limit: 25 })
          this.sendReport.sent += data.sent
          this.sendReport.failed += data.failed
          data.failed_members.forEach(m => {
            if (!failedIds.has(m.id)) {
              failedIds.add(m.id)
              this.sendReport.failedNames.push(m.name)
            }
          })
          this.sendReport.remaining = data.remaining
          if (data.processed === 0 || data.sent === 0) break // nada avanzó: evitar bucle
        }
      } catch (err) {
        this.notify(this.errorText(err, 'Error al enviar invitaciones'), 'error')
      } finally {
        this.sending = false
        await this.refresh()
      }
    },
    async resend (member, channels) {
      this.busyId = member.id
      try {
        const { data } = await tournamentsAPI.resendMember(member.id, channels)
        const sent = data.sent.map(c => (c === 'whatsapp' ? 'WhatsApp' : 'correo')).join(' y ')
        this.notify(`Enviado por ${sent} a ${member.full_name}`, 'success')
        await this.refresh()
      } catch (err) {
        const d = err.response && err.response.data
        const skipped = d && d.skipped ? Object.values(d.skipped).join('. ') : ''
        const errors = d && d.errors && d.errors.length ? d.errors.join('. ') : ''
        this.notify(errors || skipped || this.errorText(err, 'No se pudo reenviar'), 'error')
      } finally {
        this.busyId = null
      }
    },
    async markAttended (member) {
      this.busyId = member.id
      try {
        await tournamentsAPI.markAttended(member.id)
        await this.refresh()
      } catch (err) {
        this.notify(this.errorText(err, 'No se pudo marcar la asistencia'), 'error')
      } finally {
        this.busyId = null
      }
    },
    async cancelMember (member) {
      if (!window.confirm(`¿Cancelar el registro de ${member.full_name}? Su información se conserva.`)) return
      this.busyId = member.id
      try {
        await tournamentsAPI.cancelMember(member.id)
        this.notify('Registro cancelado', 'success')
        await this.refresh()
      } catch (err) {
        this.notify(this.errorText(err, 'No se pudo cancelar el registro'), 'error')
      } finally {
        this.busyId = null
      }
    },
    // ---- Utilidades ----
    copyLink () {
      navigator.clipboard.writeText(this.tournament.public_registration_url)
        .then(() => this.notify('Enlace copiado al portapapeles', 'success'))
    },
    memberStatus (status) {
      return REGISTRATION_STATUS[status] || { text: '—', color: 'grey' }
    },
    methodLabel (method) {
      return METHOD_LABELS[method] || ''
    },
    shortDate (iso) {
      return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })
    },
    errorText (err, fallback) {
      const d = err.response && err.response.data
      if (!d) return err.message || fallback
      return d.error || d.detail || fallback
    },
    notify (text, color = 'info') {
      this.snackbar = { show: true, text, color }
    }
  }
}
</script>
